/**
 * 이용 행태 분석 0단계 — Microsoft Clarity(세션 녹화·히트맵) + PostHog(이벤트·리플레이).
 * 기획: docs/ops/lay-ai-operations-plan.md §3-3 (2026-09-10 구현 기록 (아)) · 고지 문안 docs/ops/policy/analytics-notice-2026-09-10.md
 *
 * 켜는 조건 — 환경변수가 있을 때만 스크립트를 넣는다. Clarity는 웹에서만 사용하며 iOS·Android에서는 키가 있어도 실행하지 않는다.
 *   Clarity의 Microsoft 공용 광고 식별 쿠키가 ATT 없는 네이티브 앱에서 동작하지 않도록 실제 Capacitor 플랫폼으로 차단한다.
 *   PostHog의 기존 서비스 분석 설정은 웹·앱 모두 유지한다. 키가 없으면 아무것도 하지 않는다(데모·테스트·로컬 기본).
 *   REACT_APP_CLARITY_PROJECT_ID   Clarity 프로젝트 id
 *   REACT_APP_POSTHOG_KEY          PostHog 프로젝트 키 (phc_…)
 *   REACT_APP_POSTHOG_HOST         기본 https://us.i.posthog.com
 *
 * 🔒 개인정보 규칙 (처리방침 8-3·8-4 「이용 행태 분석」 항목과 한 몸)
 *   · 이메일·이름을 도구에 보내지 않는다. 식별자는 sha256(이메일) 앞 16자(distinctId) 뿐이다.
 *   · 정산·계좌·연락처·비밀번호·탈퇴 화면(SENSITIVE_PATHS)은 녹화·자동 수집을 끊는다 — Clarity 는 #root 에 data-clarity-mask,
 *     PostHog 는 stopSessionRecording + ph-no-capture. 새 민감 화면이 생기면 여기와 LayWidget NO_PICK_PATHS 둘 다에 넣는다.
 *   · 입력값은 항상 마스킹(maskAllInputs). 텍스트 마스킹 셀렉터 [data-ph-mask].
 *   · 어드민 「화면 보기」(임퍼소네이션) 중에는 식별하지 않는다 — 운영자의 조작이 크리에이터 기록으로 남지 않게.
 *   · 브라우저 「추적 안 함」(DNT/GPC)은 보지 않는다 — 대표 결정 2026-09-10 「약관에 모두 기재하고 무조건 진행, 민감 화면만 끊는다」.
 *     거부 요청은 고객센터로 받아 운영팀이 처리한다(처리방침 9-2 다).
 */

import { isNativePlatform } from "./platformUtils";

export const SENSITIVE_PATHS = ["/total", "/partner-settlement", "/partner/results", "/edit-profile", "/change-password", "/withdrawal", "/total-copyrights", "/settlement", "/reset-password"];

const CLARITY_ID = process.env.REACT_APP_CLARITY_PROJECT_ID || "";
const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY || "";
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com";

function clarityEnabled() {
  return Boolean(CLARITY_ID) && !isNativePlatform();
}

export function analyticsConfigured() {
  return Boolean(clarityEnabled() || POSTHOG_KEY);
}

/** 정산·계좌·연락처·비밀번호 화면 — 녹화·자동 수집을 끊는다 */
export function isSensitivePath(pathname) {
  const p = String(pathname || "");
  return SENSITIVE_PATHS.some((s) => p === s || p.startsWith(`${s}/`));
}

/** 도구에 보내는 식별자 — 이메일 자체가 아니라 sha256 앞 16자. 같은 사람은 같은 값, 되돌릴 수 없다. */
export async function distinctIdFor(email) {
  const e = String(email || "").trim().toLowerCase();
  if (!e) return null;
  const subtle = typeof crypto !== "undefined" && crypto.subtle;
  if (!subtle) return null;
  const digest = await subtle.digest("SHA-256", new TextEncoder().encode(e));
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, "0")).join("").slice(0, 16);
}

let started = false;

/** 스크립트 주입 — 한 번만. 키가 없으면 아무것도 하지 않는다. */
export function initAnalytics(doc = typeof document !== "undefined" ? document : null) {
  if (started || !doc || !analyticsConfigured()) return false;
  started = true;
  const w = doc.defaultView || window;
  if (clarityEnabled()) {
    // 공식 스니펫과 같은 동작: 큐를 만들고 태그를 붙인다
    w.clarity = w.clarity || function clarityQueue(...args) { (w.clarity.q = w.clarity.q || []).push(args); };
    const s = doc.createElement("script");
    s.async = true; s.src = `https://www.clarity.ms/tag/${encodeURIComponent(CLARITY_ID)}`; s.setAttribute("data-analytics", "clarity");
    doc.head.appendChild(s);
    w.clarity("set", "app", "laeebly");
  }
  if (POSTHOG_KEY) {
    // 공식 array 스니펫 축약판 — posthog-js 를 CDN 에서 받고, 그 전 호출은 큐에 쌓인다
    const ph = (w.posthog = w.posthog || []);
    if (!ph.__SV) {
      ph._i = []; ph.__SV = 1;
      const stub = (name) => (...args) => { ph.push([name, ...args]); };
      ["capture", "identify", "reset", "startSessionRecording", "stopSessionRecording", "register", "opt_out_capturing", "opt_in_capturing"].forEach((n) => { ph[n] = stub(n); });
      ph.init = (key, config) => { ph._i.push([key, config, "posthog"]); };
      const s = doc.createElement("script");
      s.async = true; s.src = `${POSTHOG_HOST.replace(/\/$/, "")}/static/array.js`; s.setAttribute("data-analytics", "posthog");
      doc.head.appendChild(s);
    }
    ph.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      autocapture: true,
      capture_pageview: false,             // 라우트 변경은 trackRoute 가 보낸다(SPA)
      capture_pageleave: true,
      person_profiles: "identified_only",
      session_recording: { maskAllInputs: true, maskTextSelector: "[data-ph-mask], .ph-mask" },
      disable_session_recording: false,
    });
  }
  return true;
}

/** 로그인 사용자 식별. 임퍼소네이션 중에는 부르지 않는다(호출부 책임). */
export async function identifyUser({ email, role, impersonating } = {}, w = typeof window !== "undefined" ? window : null) {
  if (!started || !w || impersonating) return null;
  const id = await distinctIdFor(email);
  if (!id) return null;
  const props = { role: String(role || "").slice(0, 40) || "unknown" };
  try { if (clarityEnabled() && w.clarity) { w.clarity("identify", id); w.clarity("set", "role", props.role); } } catch (e) { /* noop */ }
  try { if (POSTHOG_KEY && w.posthog) w.posthog.identify(id, props); } catch (e) { /* noop */ }
  return id;
}

/** 로그아웃 — 다음 사람과 섞이지 않게 */
export function resetAnalytics(w = typeof window !== "undefined" ? window : null) {
  if (!started || !w) return;
  try { if (POSTHOG_KEY && w.posthog) w.posthog.reset(); } catch (e) { /* noop */ }
}

let lastPath = null;
/** 라우트 변경 — 민감 화면이면 녹화·자동 수집을 끊고, 아니면 페이지뷰를 보낸다. 경로만 보내고 쿼리스트링은 뺀다. */
export function trackRoute(pathname, doc = typeof document !== "undefined" ? document : null) {
  if (!started || !doc) return { sensitive: isSensitivePath(pathname), sent: false };
  const w = doc.defaultView || window;
  const root = doc.getElementById("root") || doc.body;
  const sensitive = isSensitivePath(pathname);
  if (sensitive) {
    root.setAttribute("data-clarity-mask", "true");
    root.classList.add("ph-no-capture");
    try { if (POSTHOG_KEY && w.posthog) w.posthog.stopSessionRecording(); } catch (e) { /* noop */ }
    try { if (clarityEnabled() && w.clarity) w.clarity("set", "sensitive", "1"); } catch (e) { /* noop */ }
    lastPath = pathname;
    return { sensitive: true, sent: false };
  }
  root.removeAttribute("data-clarity-mask");
  root.classList.remove("ph-no-capture");
  try { if (POSTHOG_KEY && w.posthog) w.posthog.startSessionRecording(); } catch (e) { /* noop */ }
  if (pathname !== lastPath) {
    try { if (POSTHOG_KEY && w.posthog) w.posthog.capture("$pageview", { $current_url: `${w.location?.origin || ""}${pathname}`, path: pathname }); } catch (e) { /* noop */ }
  }
  lastPath = pathname;
  return { sensitive: false, sent: true };
}

/** 테스트 전용 — 모듈 상태 초기화 */
export function __resetForTests() { started = false; lastPath = null; }
